import React, { useState, useEffect } from "react";
import { socialApi, aiApi } from "../lib/socialApi";

const SOCIAL_LABELS = {
  facebook: "Facebook",
  instagram: "Instagram",
  youtube: "YouTube",
  x: "X",
  linkedin: "LinkedIn",
  tiktok: "TikTok",
};

/* ---------- helpers ---------- */
function normalizeTitle(post) {
  if (!post) return "Post — Untitled";
  return (
    post.title ||
    post.headline ||
    post.metaTitle ||
    "Post — Untitled"
  );
}

function normalizeContent(post) {
  if (!post) return "";

  // your planner API is storing the blog here:
  const htmlOrMd =
    post.captionHtml ||
    post.captionHTML ||
    post.html ||
    post.content ||
    post.body ||
    post.article ||
    post.text ||
    "";

  return htmlOrMd || "";
}

// simple detection: does it already look like HTML?
function looksLikeHtml(str) {
  if (!str) return false;
  return /<\/?[a-z][\s\S]*>/i.test(str.trim());
}

// very small Markdown -> HTML converter for your use-case
function markdownToHtml(md = "") {
  if (!md) return "";

  // escape HTML first
  let esc = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // bold / italic
  esc = esc
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");

  const lines = esc.split(/\r?\n/);
  let html = "";
  let inList = false;

  for (let line of lines) {
    const trimmed = line.trim();

    // lists
    if (/^[-*]\s+/.test(trimmed)) {
      if (!inList) {
        html += "<ul>";
        inList = true;
      }
      const item = trimmed.replace(/^[-*]\s+/, "");
      html += `<li>${item}</li>`;
      continue;
    } else if (inList) {
      html += "</ul>";
      inList = false;
    }

    if (!trimmed) {
      // blank line = paragraph break
      html += "";
      continue;
    }

    // headings
    if (/^###\s+/.test(trimmed)) {
      html += `<h3>${trimmed.replace(/^###\s+/, "")}</h3>`;
    } else if (/^##\s+/.test(trimmed)) {
      html += `<h2>${trimmed.replace(/^##\s+/, "")}</h2>`;
    } else if (/^#\s+/.test(trimmed)) {
      html += `<h1>${trimmed.replace(/^#\s+/, "")}</h1>`;
    } else {
      html += `<p>${trimmed}</p>`;
    }
  }

  if (inList) html += "</ul>";

  return html;
}

/* ---------- component ---------- */
export default function PlannerPostModal({ open, post, onClose, providers }) {
  // ---- hooks at top ----
  const [publishing, setPublishing] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatText, setChatText] = useState("");
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(normalizeTitle(post));
  const [content, setContent] = useState(normalizeContent(post));
  const [toast, setToast] = useState("");

  // reset when a new post is opened
  useEffect(() => {
    if (!post) return;
    setTitle(normalizeTitle(post));
    setContent(normalizeContent(post));
    setChatOpen(false);
    setChatText("");
    setToast("");
  }, [post]);

  if (!open || !post) return null;

  // derive fields
  const coverImage =
    post.coverImage ||
    post.thumbUrl ||
    post.imageUrl ||
    post.posterUrl ||
    "";

  const rawDate = post.scheduledFor || post.scheduledDate || post.date;

  const scheduledDate = rawDate
    ? new Date(rawDate).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "";

  const handlePublishClick = () => {
  const payload = {
    title,
    content,
    imageUrl: coverImage,
    // optionally keep postId if you want later
    postId: post?._id,
  };

  // Store temporarily for the next page (no huge URL)
  sessionStorage.setItem("clikkle_share_payload", JSON.stringify(payload));

  // Go to social connect WITHOUT query params
  window.location.href = "/social-connect";
};



  const handleQuickPublish = async (providerId) => {
    try {
      setPublishing(true);
      setToast("");
      await socialApi.publish({
        provider: providerId,
        postId: post._id,
        title,
        content,
        imageUrl: coverImage,
      });
      setToast(`Mock published to ${SOCIAL_LABELS[providerId]}`);
    } catch (err) {
      console.error(err);
      setToast("Failed to publish. Check console.");
    } finally {
      setPublishing(false);
    }
  };

  const handleChatSubmit = async () => {
    if (!chatText.trim()) return;
    try {
      setEditing(true);
      setToast("");
      const { updatedText } = await aiApi.editPost(content, chatText);
      setContent(updatedText); // can be HTML or Markdown
      setChatText("");
      setChatOpen(false);
      setToast("Content updated with AI edit.");
    } catch (err) {
      console.error(err);
      setToast("Failed to edit. Check console.");
    } finally {
      setEditing(false);
    }
  };

  // final HTML we render
  const htmlContent = looksLikeHtml(content)
    ? content
    : markdownToHtml(content);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40">
      {/* almost full page width & height */}
      <div className="bg-white rounded-3xl w-[96vw] max-w-[1320px] h-[90vh] overflow-hidden flex flex-col">
        {/* top bar */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="text-xs text-gray-500">{scheduledDate}</div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-lg"
          >
            ×
          </button>
        </div>

        {/* main content */}
        <div className="flex flex-1 flex-col md:flex-row min-h-0">
          {/* LEFT: image + blog content (scrolls together) */}
          <div className="flex-[3] bg-[#f7f7fb] border-r flex flex-col overflow-y-auto min-h-0">
            {/* image */}
            <div className="p-6 flex justify-center">
              {/* full width of left column so it feels connected to content */}
              <div className="w-full aspect-[16/9] bg-black rounded-3xl overflow-hidden relative shadow-lg">
                {coverImage ? (
                  <img
                    src={coverImage}
                    alt={title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white text-sm px-4 text-center">
                    Image preview
                  </div>
                )}

                <div className="absolute inset-x-6 bottom-4 text-left text-white drop-shadow">
                  <div className="font-semibold text-lg leading-snug line-clamp-2">
                    {title || "Your post headline"}
                  </div>
                </div>
              </div>
            </div>

            {/* blog content */}
            <div className="px-8 pb-8 pt-2">
              <input
                className="w-full text-lg font-semibold mb-3 outline-none bg-transparent border-b border-transparent focus:border-gray-300 pb-1"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />

              <div className="bg-white rounded-2xl p-4 text-sm text-gray-800 leading-relaxed shadow-sm">
                {htmlContent ? (
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                  />
                ) : (
                  "Your generated blog content will appear here."
                )}
              </div>

              {toast && (
                <div className="mt-3 text-xs text-blue-600">{toast}</div>
              )}
            </div>
          </div>

          {/* RIGHT: actions + Chat to Edit (scrollable) */}
          <div className="flex-[1] flex flex-col px-6 pb-5 pt-4 bg-white overflow-y-auto min-h-0">
            <div className="flex flex-col gap-3 mt-2">
              <button
                className="w-full flex justify-center items-center h-11 rounded-full bg-black text-white text-sm hover:bg-gray-800"
                onClick={() => {
                  if (coverImage) {
                    window.open(coverImage, "_blank");
                  }
                }}
              >
                Download
              </button>

              <button
                className="w-full flex justify-between items-center h-11 rounded-full border text-sm px-4"
                onClick={handlePublishClick}
              >
                <span>Publish to social media</span>
                <div className="flex items-center gap-1 text-lg">
                  <span>f</span>
                  <span>𝕏</span>
                  <span>in</span>
                  <span>▶</span>
                  <span>♫</span>
                </div>
              </button>

              {/* (optional) quick publish buttons – you can re-enable later */}
              {/* <div className="flex flex-wrap gap-2 text-xs">
                {Object.keys(SOCIAL_LABELS).map((id) => (
                  <button
                    key={id}
                    disabled={publishing}
                    onClick={() => handleQuickPublish(id)}
                    className="px-3 py-1 rounded-full border bg-gray-50 hover:bg-gray-100 disabled:opacity-50"
                  >
                    Post to {SOCIAL_LABELS[id]}
                  </button>
                ))}
              </div> */}

              {/* Chat to Edit */}
              <button
                className="w-full flex justify-center items-center h-11 rounded-full border text-sm mt-1"
                onClick={() => setChatOpen((v) => !v)}
              >
                Chat to Edit
              </button>

              {chatOpen && (
                <div className="border rounded-2xl p-3 text-sm mt-2">
                  <div className="text-xs text-gray-500 mb-1">
                    Tell the AI what to change. Example: “Shorten to 2
                    sentences and make it more playful.”
                  </div>
                  <textarea
                    className="w-full border rounded-xl px-2 py-1 text-sm mb-2 min-h-[60px] outline-none focus:border-gray-400"
                    value={chatText}
                    onChange={(e) => setChatText(e.target.value)}
                    placeholder="Type to edit post..."
                  />
                  <button
                    className="px-4 py-1 rounded-full bg-black text-white text-xs disabled:opacity-60"
                    disabled={editing}
                    onClick={handleChatSubmit}
                  >
                    {editing ? "Applying..." : "Apply changes"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
